import { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { useScreenSizeOnly } from 'hooks/useScreenSize'
import PreviewableModal from '../../../../Tripkit/PreviewableModal/PreviewableModal'
import DesktopOfferMapCard from './DesktopOfferMapCard'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalContent from 'LuxKit/Modal/ModalContent'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import offerPageURL from 'lib/offer/offerPageURL'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import TripContext from 'tripPlanner/contexts/TripContext'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import { HotelBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import type { AccommodationItem } from 'tripPlanner/types/tripItem'
import { buildMyEscapesOrderLink, formatShortDateRange, isLeBooking, isLeBookmark } from 'tripPlanner/utils'
import useTripPlannerCTALabels from 'tripPlanner/hooks/useTripPlannerCTALabels'
import SavedItemRemoveButton from 'tripPlanner/components/ItemCard/Common/SavedItemRemoveButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import NotesAndAttachments from './NotesAndAttachments'
import Divider from 'LuxKit/Divider'
import LEOfferPriceDetails from 'tripPlanner/components/ItemOfferPrice/LEOfferPriceDetails'
import { findHotelPackage } from 'tripPlanner/utils/offer'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import { nonNullable } from 'lib/array/arrayUtils'
import useAccommodationItemOccupancyDescription from 'tripPlanner/hooks/occupancy/useAccommodationItemOccupancyDescription'
import { useIntl } from 'react-intl'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import LineNotesIcon from 'LuxKit/Icons/line/LineNotesIcon'
import MarkdownRender from 'LuxKit/MarkdownRender'

const SrcImage = styled.img`
  height: ${rem(120)};
  width: auto;
`

const CarouselContainer = styled.div`
  padding-bottom: ${rem(20)};
`

const Flex1 = styled.div`
  flex: 1;
`

interface Props {
  offer: App.HotelOffer
  tripItem?: AccommodationItem
  onClose: () => void
}

function LEHotelSummaryCard({ offer, tripItem, onClose }: Props) {
  const intl = useIntl()
  const { bookingDetailsCopy } = useTripPlannerCTALabels()
  const isMobile = useScreenSizeOnly('mobile')
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const canEdit = useCanEditTrip()
  const tripContext = useContext(TripContext)
  const tripId = tripContext?.tripId

  const onClickViewOffer = useCallback(() => {
    if (tripId) {
      setImmersiveMode({
        tripId,
      })
    }
  }, [tripId, setImmersiveMode])

  const carousel = <CardCarousel gap={8} gutterSize={20} desktopGutterSize={24} gutterStyle="overflow">
    {offer.images.map(image => (
      image.id ?
          <Image key={image.id} id={image.id} alt={image.title} height={120} /> :
          <SrcImage key={image.url} src={image.url} alt={image.title} />
    ))}
  </CardCarousel>

  const bookingID = tripItem && isLeBooking(tripItem) && canEdit ? tripItem.confirmationCode : undefined

  const bookmarkButton = tripItem ? <SavedItemRemoveButton item={tripItem} /> : <HotelBookmarkButton offer={offer} withLabel="never" size="small" />

  const timeFormat = useRegionTimeFormat()
  const { occupanciesText } = useAccommodationItemOccupancyDescription(tripItem)
  let timeAndOccupancyText: string | undefined = undefined
  let checkInText: string | undefined = undefined
  if (tripItem) {
    if (tripItem.startTime) {
      const formattedTime = tripItem.startTime.format(timeFormat)
      checkInText = isLeBookmark(tripItem)
        ? intl.formatMessage(
            {
              id: 'kfaaYKSXyWgALU5xpj5V',
              defaultMessage: 'Check-in from {time}',
              description: 'Hotel check-in time range starting from a specific time on trip map summary card',
            },
            { time: formattedTime },
          )
        : intl.formatMessage(
            {
              id: 'IyflZRNvHU9XvXfaI1Wd',
              defaultMessage: 'Check-in at {time}',
              description: 'Hotel check-in time at a specific time on trip map summary card',
            },
            { time: formattedTime },
          )
    }
    const timeDescription = formatShortDateRange(tripItem)
    timeAndOccupancyText = nonNullable([timeDescription, occupanciesText])
      .filter((text) => text.length > 0)
      .join(', ')
  }

  const mainContent = <>
    <Group direction="horizontal" gap={8}>
      <Flex1>
        <VerticalSpacer gap={8}>
          {checkInText && (
            <BodyTextBlock variant="medium" colour="neutral-three" startIcon={<LineClockIcon />}>
              {checkInText}
            </BodyTextBlock>
          )}
          {offer.property.address && (
            <BodyTextBlock
              variant="medium"
              startIcon={<LineMapMarkerIcon />}
              weight="bold"
              wrap="word-wrap"
            >
              {offer.property.address}
            </BodyTextBlock>
          )}
          {timeAndOccupancyText && (
            <BodyTextBlock variant="medium" startIcon={<LineNotesIcon />}>
              {timeAndOccupancyText}
            </BodyTextBlock>
          )}
          {tripItem?.isBooked && <CSSBreakpoint max="mobile">
            <BookedLabel />
          </CSSBreakpoint>}
        </VerticalSpacer>
      </Flex1>
      <CSSBreakpoint max="mobile">
        {bookmarkButton}
      </CSSBreakpoint>
    </Group>

    <LargeContentToggle
      maxHeight={80}
      collapsedHeight={60}
      showMoreText={intl.formatMessage({
        id: 'oNsJ4yKSKB9sTbcHkh7C',
        defaultMessage: 'Read more',
        description: 'Toggle button text to expand hotel summary content on trip map card',
      })}
      showLessText={intl.formatMessage({
        id: 'phpzY9MFudiG-SJK-3Zm',
        defaultMessage: 'Read less',
        description: 'Toggle button text to collapse hotel summary content on trip map card',
      })}
      showChevron={false}
    >
      <VerticalSpacer gap={12}>
        {offer.tileInclusions && offer.tileInclusions.length > 0 && (
          <VerticalSpacer gap={2}>
            {offer.tileInclusions.map(inclusion => (
              <OfferInclusionItem
                key={inclusion.id}
                inclusion={inclusion}
                isHighlighted={inclusion.isHighlighted}
                showMinStayDescription
              />
            ))}
          </VerticalSpacer>
        )}
        <MarkdownRender type="compact" content={offer.description} fontSize="medium" />
      </VerticalSpacer>
    </LargeContentToggle>

    {tripItem && <>
      <Divider kind="primary" />
      <NotesAndAttachments item={tripItem} canEdit={canEdit} />
    </>}
  </>

  const bookingLink = !!bookingID && tripItem && <TextButton
    kind="secondary"
    size="large"
    to={buildMyEscapesOrderLink(tripItem)}
  >
    {bookingDetailsCopy}
  </TextButton>

  const footer = <ModalFooter>
    <Group
      direction={bookingLink ? 'vertical' : 'horizontal'}
      desktopDirection="horizontal"
      horizontalAlign="space-between"
      verticalAlign={bookingLink ? 'start' : 'center'}
      desktopVerticalAlign="center"
      gap={12}
      wrap="wrap"
    >
      {!tripItem?.isBooked &&
        <LEOfferPriceDetails
          offer={offer}
          package={tripItem && findHotelPackage(offer.packages, tripItem)}
        />}

      <Group direction="horizontal" horizontalAlign="center" gap={12} wrap="wrap">
        <TextButton kind="tertiary" size="large" to={offerPageURL(offer)} onClick={onClickViewOffer}>
          {intl.formatMessage({
            id: 'clFNvmdqwY8tSizlvTyr',
            defaultMessage: 'View offer',
            description: 'Button text to view offer on trip map summary card',
          })}
        </TextButton>
        {bookingLink}
      </Group>
    </Group>
  </ModalFooter>

  if (isMobile) {
    return (
      <PreviewableModal onRequestClose={onClose}>
        <ModalHeader onCloseButtonClick={onClose} title={offer.property.name} />
        <ModalBody>
          <ModalContent>
            <CarouselContainer>
              {carousel}
            </CarouselContainer>
            <VerticalSpacer gap={20}>
              {mainContent}
            </VerticalSpacer>
          </ModalContent>
        </ModalBody>
        {footer}
      </PreviewableModal>
    )
  }

  return (
    <DesktopOfferMapCard>
      <ModalBody>
        <ModalContent>
          <CarouselContainer>
            {carousel}
          </CarouselContainer>
          <VerticalSpacer gap={20}>
            <HeadingTextBlock
              variant="heading5"
              endIcon={<Group direction="horizontal" gap={12} verticalAlign="center">
                {tripItem?.isBooked && <BookedLabel />}
                {bookmarkButton}
              </Group>}
              fit="stretch"
            >
              {offer.property.name}
            </HeadingTextBlock>
            {mainContent}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      {footer}
    </DesktopOfferMapCard>
  )
}

export default LEHotelSummaryCard
